/**
 * Offline-First Data Layer
 *
 * This is the main entry point for all data collection saves.
 * It implements the offline-first pattern:
 * 1. ALWAYS saves to IndexedDB first with "pending" status
 * 2. Triggers background sync if online
 * 3. Returns immediately after IndexedDB save (fast UX)
 *
 * Usage:
 *   import { saveDataEntry } from 'utils/offlineFirstDataLayer';
 *   const entryId = await saveDataEntry('cold-probe', formData, metadata);
 */

import {
  saveOfflineEntry,
  getOfflineEntries,
  getUnsyncedEntries,
  updateOfflineEntry,
  deleteOfflineEntry,
  FORM_TYPES
} from './offlineDB';
import { triggerSync, SYNC_STATUS, getSyncStatus, promoteDraftEntries } from './syncManager';

// Debounce timer for sync triggering
let syncDebounceTimer = null;
const SYNC_DEBOUNCE_MS = 1000; // Wait 1 second after last save before syncing

/**
 * Debounced sync trigger - waits for saves to stop before syncing
 * This prevents multiple sync attempts when saving rapidly
 */
const debouncedTriggerSync = () => {
  // Clear any pending sync
  if (syncDebounceTimer) {
    clearTimeout(syncDebounceTimer);
  }

  // Schedule sync after debounce period
  syncDebounceTimer = setTimeout(() => {
    syncDebounceTimer = null;
    const { isOnline } = getSyncStatus();
    if (isOnline) {
      triggerSync().catch((error) => {
        console.warn('[OfflineFirst] Background sync failed:', error.message);
      });
    }
  }, SYNC_DEBOUNCE_MS);
};

/**
 * Save a data collection entry using the offline-first pattern
 *
 * This function:
 * 1. Saves the entry to IndexedDB with "pending" sync status
 * 2. Returns immediately with the entry ID
 * 3. Triggers background sync if online
 *
 * @param {string} formType - The form type (from FORM_TYPES)
 * @param {Object} data - The form data to save
 * @param {Object} metadata - Additional metadata
 * @param {number} metadata.childId - Child ID
 * @param {string} metadata.childName - Child name
 * @param {number} metadata.therapistId - Therapist ID
 * @param {string} metadata.therapistName - Therapist name
 * @param {string} [metadata.sessionId] - Session UUID (required for API sync)
 * @param {string} [metadata.sessionDate] - Session date (defaults to now)
 * @param {Object} [options] - Additional options
 * @param {boolean} [options.triggerSync=true] - Whether to trigger background sync
 * @returns {Promise<number>} The IndexedDB entry ID
 */
export const saveDataEntry = async (formType, data, metadata, options = {}) => {
  const { triggerSync: shouldTriggerSync = true } = options;

  // Prepare the entry
  // Use DRAFT status when triggerSync is false — this prevents the background
  // sync manager from picking up entries during active data collection.
  // Entries are promoted to PENDING when forceSyncAll() is called at session end.
  const entry = {
    formType,
    childId: metadata.childId,
    childName: metadata.childName,
    therapistId: metadata.therapistId,
    therapistName: metadata.therapistName,
    sessionId: metadata.sessionId || null,
    sessionDate: metadata.sessionDate || new Date().toISOString(),
    data,
    // STABLE DEDUPE KEY. When the caller supplies an offlineId, carry it on the entry so
    // syncManager sends it as offline_id and the backend upserts by (offline_id,
    // session_id) instead of inserting. Callers that collect ONE accumulating sheet per
    // (session, collector, form) pass a stable key here, so a page refresh — which wipes
    // the in-memory entry-id map — re-sends the same key and UPDATES the existing row
    // rather than creating a duplicate. Callers that file independent rows (e.g. injury
    // reports) omit it, and syncManager falls back to the per-row id as before.
    offlineId: metadata.offlineId || null,
    // Sync status fields
    syncStatus: shouldTriggerSync ? SYNC_STATUS.PENDING : SYNC_STATUS.DRAFT,
    syncError: null,
    syncAttempts: 0,
    lastSyncAttempt: null
  };

  try {
    // 1. Save to IndexedDB first (this is the "offline-first" part)
    const entryId = await saveOfflineEntry(entry);

    // 2. Trigger debounced background sync if requested
    // Using debounce prevents multiple sync attempts when saving rapidly
    if (shouldTriggerSync) {
      debouncedTriggerSync();
    }

    return entryId;
  } catch (error) {
    console.error('[OfflineFirst] Failed to save entry:', error);
    throw error;
  }
};

/**
 * Update an existing data entry
 *
 * @param {number} entryId - The entry ID to update
 * @param {Object} data - Updated form data
 * @param {Object} [options] - Additional options
 * @returns {Promise<Object>} The updated entry
 */
export const updateDataEntry = async (entryId, data, options = {}) => {
  const { triggerSync: shouldTriggerSync = true } = options;

  try {
    // Reset sync status when data is updated.
    // Use DRAFT when triggerSync is false to prevent background sync during active editing.
    const updatedEntry = await updateOfflineEntry(entryId, {
      data,
      syncStatus: shouldTriggerSync ? SYNC_STATUS.PENDING : SYNC_STATUS.DRAFT,
      syncError: null,
      updatedAt: new Date().toISOString()
    });

    // Trigger debounced background sync if requested
    if (shouldTriggerSync) {
      debouncedTriggerSync();
    }

    return updatedEntry;
  } catch (error) {
    console.error('[OfflineFirst] Failed to update entry:', error);
    throw error;
  }
};

/**
 * Delete a data entry
 *
 * @param {number} entryId - The entry ID to delete
 * @returns {Promise<void>}
 */
export const deleteDataEntry = async (entryId) => {
  try {
    await deleteOfflineEntry(entryId);
  } catch (error) {
    console.error('[OfflineFirst] Failed to delete entry:', error);
    throw error;
  }
};

/**
 * Get all entries for a specific form type and child
 *
 * @param {string} formType - The form type
 * @param {number} childId - The child ID
 * @returns {Promise<Array>} List of entries
 */
export const getEntriesForChild = async (formType, childId) => {
  return getOfflineEntries(formType, childId);
};

/**
 * Get sync status for a specific entry
 *
 * @param {number} entryId - The entry ID
 * @returns {Promise<Object>} Sync status info
 */
export const getEntrySyncStatus = async (entryId) => {
  const entries = await getUnsyncedEntries();
  const entry = entries.find((e) => e.id === entryId);

  if (!entry) {
    return { status: SYNC_STATUS.SYNCED, synced: true };
  }

  return {
    status: entry.syncStatus || SYNC_STATUS.PENDING,
    synced: entry.synced || false,
    attempts: entry.syncAttempts || 0,
    error: entry.syncError,
    lastAttempt: entry.lastSyncAttempt
  };
};

/**
 * Get summary of all pending entries
 *
 * @returns {Promise<Object>} Summary of pending entries by form type
 */
export const getPendingSummary = async () => {
  const entries = await getUnsyncedEntries();

  const summary = {
    total: entries.length,
    byFormType: {},
    byStatus: {
      [SYNC_STATUS.PENDING]: 0,
      [SYNC_STATUS.SYNCING]: 0,
      [SYNC_STATUS.FAILED]: 0
    }
  };

  entries.forEach((entry) => {
    // Count by form type
    if (!summary.byFormType[entry.formType]) {
      summary.byFormType[entry.formType] = 0;
    }
    summary.byFormType[entry.formType]++;

    // Count by status
    const status = entry.syncStatus || SYNC_STATUS.PENDING;
    if (summary.byStatus[status] !== undefined) {
      summary.byStatus[status]++;
    }
  });

  return summary;
};

/**
 * Force sync all pending entries
 * Promotes DRAFT entries to PENDING first, then triggers sync.
 * Used by "End Session" and manual "Sync Now" buttons.
 *
 * @returns {Promise<Object>} Sync results
 */
export const forceSyncAll = async () => {
  // First promote any draft entries to pending so they get picked up
  await promoteDraftEntries();
  return triggerSync();
};

// Re-export commonly used items
export { FORM_TYPES, SYNC_STATUS };

export default {
  saveDataEntry,
  updateDataEntry,
  deleteDataEntry,
  getEntriesForChild,
  getEntrySyncStatus,
  getPendingSummary,
  forceSyncAll,
  FORM_TYPES,
  SYNC_STATUS
};
